import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Ciclo di aggiornamento della PWA.
 *
 * Il programma che gira nel browser arriva dalla cache del service worker: finche non si
 * ricarica la pagina, un dispositivo puo restare per giorni su una versione vecchia, con
 * correzioni gia pubblicate che li non ci sono. Qui la versione nuova viene cercata da sola,
 * e quando e pronta l'app lo dice invece di aspettare che l'utente ricarichi a mano.
 *
 * La pagina si ricarica SOLO dopo che l'utente ha toccato il pulsante: un ricaricamento a
 * sorpresa mentre si compila un modulo farebbe perdere il lavoro scritto.
 */

export const SERVICE_WORKER_URL = "/sw.js";
const CHECK_INTERVAL_MS = 60_000;

export type AppUpdateOptions = {
  /** Iniettabile per le prove: in esecuzione vera e quello del browser. */
  container?: ServiceWorkerContainer | null;
  reload?: () => void;
  checkIntervalMs?: number;
};

function browserContainer(): ServiceWorkerContainer | null {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker;
}

export function useAppUpdate(options: AppUpdateOptions = {}) {
  const { container = browserContainer(), reload, checkIntervalMs = CHECK_INTERVAL_MS } = options;
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState<ServiceWorker | null>(null);
  const accepted = useRef(false);
  const reloaded = useRef(false);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!container) return;
    let cancelled = false;
    let registration: ServiceWorkerRegistration | null = null;
    let timer: ReturnType<typeof setInterval> | undefined;
    const detach: Array<() => void> = [];

    // Una versione nuova conta solo se una vecchia stava gia servendo la pagina:
    // alla primissima installazione non c'e niente da aggiornare e nessun avviso da dare.
    const announce = (worker: ServiceWorker | null | undefined) => {
      if (!worker || !container.controller) return;
      setWaiting(worker);
    };

    const watchInstall = (target: ServiceWorkerRegistration) => {
      const onUpdateFound = () => {
        const installing = target.installing;
        if (!installing) return;
        const onStateChange = () => {
          if (installing.state === "installed") announce(target.waiting ?? installing);
        };
        installing.addEventListener("statechange", onStateChange);
        detach.push(() => installing.removeEventListener("statechange", onStateChange));
      };
      target.addEventListener("updatefound", onUpdateFound);
      detach.push(() => target.removeEventListener("updatefound", onUpdateFound));
    };

    const onControllerChange = () => {
      if (!accepted.current || reloaded.current) return;
      reloaded.current = true;
      (reloadRef.current ?? (() => window.location.reload()))();
    };
    container.addEventListener("controllerchange", onControllerChange);

    // Una PWA installata puo restare aperta per giorni: il controllo al ritorno in primo piano
    // e quello che intercetta la versione nuova senza che l'utente debba ricaricare.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") return;
      void registration?.update().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    void container
      .register(SERVICE_WORKER_URL, { scope: "/" })
      .then((registered) => {
        if (cancelled) return;
        registration = registered;
        watchInstall(registered);
        announce(registered.waiting);
        timer = setInterval(() => void registered.update().catch(() => {}), checkIntervalMs);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      container.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      for (const stop of detach) stop();
    };
  }, [container, checkIntervalMs]);

  const applyUpdate = useCallback(() => {
    accepted.current = true;
    waiting?.postMessage({ type: "SKIP_WAITING" });
  }, [waiting]);

  const dismiss = useCallback(() => setDismissed(waiting), [waiting]);

  return { updateReady: waiting !== null && waiting !== dismissed, applyUpdate, dismiss };
}
